import { Injectable } from '@angular/core';
import { WindowRefService } from '@app/core/services/window.service';
import { DashboardAction } from '../enums';
import AuthenticationFacade from '@app/core/facades/authentication.facade';
import { LoginFacade } from '@app/modules/auth/facades/login-facade';
import { take } from 'rxjs/operators';
import { DashboardMessage } from '../models';
import { STORAGE_KEY } from '@app/core/config/app.config';

const EVENT_TYPE = 'FROM_DASHBOARD';
const IS_EXTENSION_INSTALLED_TIMEOUT_MS = 2000;

@Injectable()
export class ExtensionCommunicationService {
  private readonly _window: Window;
  private _isLoggedIn: boolean;
  private _latestDashboardMessage: DashboardMessage | null = null;

  constructor(
    private readonly _authFacade: AuthenticationFacade,
    private readonly _loginFacade: LoginFacade,
    windowRefService: WindowRefService
  ) {
    this._window = windowRefService.nativeWindow;
    this._authFacade.isLoggedIn$.pipe(take(1)).subscribe((isLoggedIn) => {
      this._isLoggedIn = isLoggedIn;
    });
    this._latestDashboardMessage = JSON.parse(localStorage.getItem(STORAGE_KEY.latestDashboardMessage));
    this._addListeners();
    this._sendDashboardReady();
  }

  public isExtensionInstalled(): Promise<boolean> {
    const message: Promise<boolean> = new Promise((resolve) => {
      this._addListener(DashboardAction.IsExtensionInstalledResponse, () => {
        resolve(true);
      });
      this._sendDashboardMessage({ action: DashboardAction.IsExtensionInstalled }, false);
    });
    const timeout: Promise<boolean> = new Promise((resolve) => {
      setTimeout(resolve, IS_EXTENSION_INSTALLED_TIMEOUT_MS, false);
    });

    return Promise.race([message, timeout]);
  }

  public waitForExtensionInstallation(): Promise<void> {
    return new Promise((resolve) => {
      this._addListener(DashboardAction.ExtensionInstalled, () => {
        resolve();
      });
    });
  }

  public sendDashboardLogin(username: string, password: string): void {
    this._sendDashboardMessage({ action: DashboardAction.DashboardSignedIn, detail: { username, password } });
  }

  public sendDashboardLogout(): void {
    this._sendDashboardMessage({ action: DashboardAction.DashboardSignedOut });
  }

  public sendLatestDashboardMessage(): void {
    if (this._latestDashboardMessage) {
      this._sendDashboardMessage(this._latestDashboardMessage, false);
      this._latestDashboardMessage = null;
    }
  }

  private _sendDashboardReady(): void {
    this._sendDashboardMessage({ action: DashboardAction.DashboardReady }, false);
  }

  private _sendDashboardMessage(dashboardMessage: DashboardMessage, setLatest = true): void {
    if (setLatest) {
      this._setLatestDashboardMessage(dashboardMessage);
    }
    this._window.postMessage(
      { type: EVENT_TYPE, action: dashboardMessage.action, detail: dashboardMessage.detail },
      this._window.location.origin
    );
  }

  private _setLatestDashboardMessage(dashboardMessage: DashboardMessage): void {
    this._latestDashboardMessage = dashboardMessage;
    localStorage.setItem(STORAGE_KEY.latestDashboardMessage, JSON.stringify(dashboardMessage));
  }

  private _addListeners(): void {
    this._addExtensionSignedInListener();
    this._addExtensionSignedOutListener();
  }

  private _addExtensionSignedInListener(): void {
    this._addListener(DashboardAction.ExtensionSignedIn, (event) => {
      if (this._isLoggedIn) {
        return;
      }
      this._loginFacade.login(event.data.detail.username, event.data.detail.password);
    });
  }

  private _addExtensionSignedOutListener(): void {
    this._addListener(DashboardAction.ExtensionSignedOut, () => {
      if (!this._isLoggedIn) {
        return;
      }
      this._authFacade.logout();
    });
  }

  private _addListener(action: DashboardAction, callback?: (event: MessageEvent) => void): void {
    const listener = (event: MessageEvent) => {
      if (event.data.type === EVENT_TYPE && event.data.action === action) {
        callback(event);
        this._window.removeEventListener('message', listener);
      }
    };
    this._window.addEventListener('message', listener);
  }
}
